import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { motion } from 'framer-motion';
import { Brain, Flame, CheckCircle2, ShieldCheck, ArrowRight, GraduationCap, Bot, Users, PhoneCall, Gift } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { BonusCard } from '../components/BonusCard';
import { TestimonialSection } from '../components/TestimonialSection';
import { Footer } from '../components/Footer';
import { FAQ } from '../components/FAQ';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
declare global {
  interface Window {
    fbq: any;
  }
}
export function ReplayOfferPage() {
  const [showCta, setShowCta] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [giftTimeLeft, setGiftTimeLeft] = useState(1500); // 25 minutes in seconds
  const [giftCountdownStarted, setGiftCountdownStarted] = useState(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const contentTimerRef = useRef<NodeJS.Timeout | null>(null);
  const giftIntervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    // Start gift countdown immediately on page load
    giftIntervalRef.current = setInterval(() => {
      setGiftTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(giftIntervalRef.current!);
          giftIntervalRef.current = null;
          setShowGift(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    // Load Vimeo Player API
    const vimeoScript = document.createElement('script');
    vimeoScript.src = 'https://player.vimeo.com/api/player.js';
    vimeoScript.async = true;
    vimeoScript.onload = () => {
      const iframe = document.getElementById('replay-offer-video') as HTMLIFrameElement;
      if (iframe && (window as any).Vimeo) {
        const player = new (window as any).Vimeo.Player(iframe);
        player.on('play', () => {
          if (!contentTimerRef.current) {
            // 24 minutes = 1440 seconds
            contentTimerRef.current = setTimeout(() => {
              setShowContent(true);
            }, 1440000);
          }
          if (!timerRef.current) {
            // 1 hour 2 minutes = 3720 seconds
            timerRef.current = setTimeout(() => {
              setShowCta(true);
            }, 3720000);
          }
        });
      }
    };
    document.head.appendChild(vimeoScript);
    (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);
    window.fbq('init', '754551913878347');
    window.fbq('track', 'PageView');
    window.fbq('trackCustom', 'Webinar-Replay-Offer');
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (contentTimerRef.current) clearTimeout(contentTimerRef.current);
      if (giftIntervalRef.current) clearInterval(giftIntervalRef.current);
      if (document.head.contains(vimeoScript)) document.head.removeChild(vimeoScript);
    };
  }, []);
  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-black font-sans selection:bg-cyan-500/30" data-id="element-2656">
      {/* 1. Urgency Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 overflow-hidden whitespace-nowrap shadow-md" data-id="element-2657">
        <div className="inline-block animate-marquee py-2 text-white text-sm md:text-base font-bold tracking-wide" data-id="element-2658">
          <span className="mx-8" data-id="element-2659">
            ⚠️ La grabación de la Masterclass "48 Hrs AI Cash Machine" expira en
            48 horas. No habrá otra oportunidad. ⚠️
          </span>
          <span className="mx-8" data-id="element-2660">
            ⚠️ La grabación de la Masterclass "48 Hrs AI Cash Machine" expira en
            48 horas. No habrá otra oportunidad. ⚠️
          </span>
          <span className="mx-8" data-id="element-2661">
            ⚠️ La grabación de la Masterclass "48 Hrs AI Cash Machine" expira en
            48 horas. No habrá otra oportunidad. ⚠️
          </span>
        </div>
      </div>

      {/* 2. Hero/Headline */}
      <section className="pt-14 pb-8 md:pt-32 md:pb-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-2662">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-900 to-slate-900" data-id="element-2663"></div>
        <div className="container mx-auto px-4 relative z-10 max-w-5xl text-center" data-id="element-2664">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="inline-block mb-3 md:mb-6" data-id="element-2665">
            <span className="bg-red-500/10 text-red-500 border border-red-500/20 px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase" data-id="element-2666">
              [GRABACIÓN]
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl md:text-5xl lg:text-6xl font-black tracking-tight mb-3 md:mb-6 leading-tight" data-id="element-2667">
            Cómo Lanzar Tu Primer Producto Digital Este Fin de Semana{' '}
            <span className="text-cyan-400 block mt-1 md:mt-2" data-id="element-2668">
              (Aunque No Tengas Idea de Qué Vender)
            </span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-6 md:mb-12" data-id="element-2669">
            Acceso por tiempo limitado a la Masterclass en vivo donde Gabriel
            Beltrán te muestra el sistema de IA que elimina la improvisación y
            te dice exactamente qué hacer, en qué orden y qué ignorar para
            lanzar un negocio digital rentable desde cero.
          </motion.p>

          {/* 3. Video Embed */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative max-w-4xl mx-auto" data-id="element-2670">
            <div className="relative bg-slate-950 rounded-xl overflow-hidden border border-slate-800" style={{
            boxShadow: '0 0 0 1px rgba(6, 182, 212, 0.3), 0 0 20px rgba(6, 182, 212, 0.2), 0 0 40px rgba(6, 182, 212, 0.1)',
            padding: '56.25% 0 0 0',
            position: 'relative'
          }} data-id="element-2671">
              <iframe id="replay-offer-video" src="https://player.vimeo.com/video/1183969201?badge=0&autopause=0&player_id=0&app_id=58479&controls=1" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }} title="Cómo dejar de improvisar y lanzar un producto digital en 48 horas con un sistema de IA que decide por ti." data-id="element-2672" />
            </div>
          </motion.div>

          {/* Gift Countdown Timer - Below Video */}
          {giftCountdownStarted && !showGift && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mt-6 md:mt-8 max-w-4xl mx-auto" data-id="element-2673">
              <div className="bg-slate-800/50 border border-cyan-500/30 rounded-xl p-4 md:p-6 text-center backdrop-blur-sm" style={{
            boxShadow: '0 0 20px rgba(6, 182, 212, 0.1)'
          }} data-id="element-2674">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-2675">
                  <Gift className="w-5 h-5 text-cyan-400 animate-pulse" data-id="element-2676" />
                  <p className="text-cyan-400 font-bold text-sm md:text-base uppercase tracking-wider" data-id="element-2677">
                    🎁 Destrancamos un regalo gratis valorado en $297 en:
                  </p>
                  <Gift className="w-5 h-5 text-cyan-400 animate-pulse" data-id="element-2678" />
                </div>
                <div className="text-3xl md:text-5xl font-black text-white font-mono tracking-widest" data-id="element-2679">
                  {formatTime(giftTimeLeft)}
                </div>
                <p className="text-slate-400 text-xs md:text-sm mt-2" data-id="element-2680">
                  Sigue viendo para desbloquear tu regalo exclusivo
                </p>
              </div>
            </motion.div>}

          {/* Gift CTA - Revealed after 25 minutes */}
          {showGift && <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          type: 'spring'
        }} className="mt-6 md:mt-8 max-w-4xl mx-auto" data-id="element-2681">
              <div className="bg-slate-800/50 border-2 border-cyan-500/50 rounded-xl p-5 md:p-8 text-center relative overflow-hidden backdrop-blur-sm" style={{
            boxShadow: '0 0 30px rgba(6, 182, 212, 0.15)'
          }} data-id="element-2682">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-500/5 via-transparent to-transparent" data-id="element-2683"></div>
                <div className="relative z-10" data-id="element-2684">
                  <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 px-4 py-1.5 rounded-full mb-3" data-id="element-2685">
                    <Gift className="w-4 h-4 text-cyan-400" data-id="element-2686" />
                    <span className="text-cyan-400 font-bold text-xs uppercase tracking-widest" data-id="element-2687">
                      🎉 Regalo Desbloqueado
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white mb-2" data-id="element-2688">
                    ¡Felicidades! Tu Regalo Valorado en{' '}
                    <span className="text-cyan-400" data-id="element-2689">$297</span> Está Listo
                  </h3>
                  <p className="text-slate-300 text-sm md:text-base mb-5 max-w-lg mx-auto" data-id="element-2690">
                    Accede gratis a nuestras meditaciones exclusivas diseñadas
                    para emprendedores digitales.
                  </p>
                  <a href="https://48hrscashmachine.com/meditations" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-2691">
                    <button className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-900 font-black py-3.5 px-10 md:py-4 md:px-14 rounded-xl text-base md:text-lg uppercase tracking-wide shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/40 transition-all duration-300 flex items-center justify-center gap-2 hover:scale-[1.02]" data-id="element-2692">
                      <Gift className="w-5 h-5" data-id="element-2693" />
                      RECLAMAR MI REGALO GRATIS
                      <ArrowRight className="w-5 h-5" data-id="element-2694" />
                    </button>
                  </a>
                </div>
              </div>
            </motion.div>}

          {showCta && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mt-6 md:mt-10 text-center" data-id="element-2695">
              <a href="#pricing" className="inline-block" data-id="element-2696">
                <button className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-900 font-black py-3.5 px-10 md:py-4 md:px-14 rounded-xl text-base md:text-lg uppercase tracking-wide shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/40 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-2697">
                  QUIERO ACCEDER AL SISTEMA
                  <ArrowRight className="w-5 h-5" data-id="element-2698" />
                </button>
              </a>
            </motion.div>}
        </div>
      </section>

      {showContent && <>
          {/* 7. Testimonials - Moved above the fold */}
          <section className="py-10 md:py-20 bg-white border-t border-slate-100" data-id="element-2699">
            <div className="container mx-auto px-4" data-id="element-2700">
              <motion.div {...fadeIn} data-id="element-2701">
                <TestimonialSection data-id="element-2702" />
              </motion.div>
            </div>
          </section>

          {/* 7.5 Mobile-Only Screenshot Testimonials */}
          <section className="py-8 bg-slate-900 md:hidden" data-id="element-2703">
            <div className="px-4" data-id="element-2704">
              <motion.div {...fadeIn} className="text-center mb-6" data-id="element-2705">
                <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase inline-block" data-id="element-2706">
                  Resultados en las Primeras 48 Hrs
                </span>
              </motion.div>
              <div className="space-y-4" data-id="element-2707">
                {['https://cdn.magicpatterns.com/uploads/6hDRsi8geoyBo1LuTkW1VC/1.png', 'https://cdn.magicpatterns.com/uploads/jBgTZy1aGZp3HXsx6V1XFc/2.png', 'https://cdn.magicpatterns.com/uploads/9SvJ47R5DxiQD9fcm6e3iP/3.png', 'https://cdn.magicpatterns.com/uploads/pLaiYtszVfzAtTQgTPmXP5/4.png', 'https://cdn.magicpatterns.com/uploads/edMRnj3do87UGZUb3vBuUJ/5.png', 'https://cdn.magicpatterns.com/uploads/qfNfNLiDXsWz23HitwxznD/6.png', 'https://cdn.magicpatterns.com/uploads/9394kMK1jJMHkXQoyyXkjr/7.png', 'https://cdn.magicpatterns.com/uploads/2WbYgSsRnKA1qq61jawuZh/8.png'].map((src, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.08
            }} className="relative rounded-xl overflow-hidden border border-cyan-500/10" data-id="element-2708">
                    <img src={src} alt={`Testimonio ${i + 1}`} className="w-full h-auto block" loading="lazy" data-id="element-2709" />
                    {/* Branded cyan tint overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.06] via-transparent to-cyan-900/[0.12] pointer-events-none" data-id="element-2710" />
                    <div className="absolute inset-0 mix-blend-color bg-cyan-500/[0.04] pointer-events-none" data-id="element-2711" />
                  </motion.div>)}
              </div>
            </div>
          </section>

          {/* 4. The Problem */}
          <section className="py-10 md:py-20 bg-white" data-id="element-2712">
            <div className="container mx-auto px-4 max-w-5xl" data-id="element-2713">
              <motion.div {...fadeIn} className="text-center mb-8 md:mb-16" data-id="element-2714">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 max-w-3xl mx-auto leading-tight" data-id="element-2715">
                  Si te perdiste la masterclass en vivo, probablemente es porque
                  estás atrapado en uno de estos dos ciclos:
                </h2>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-4 md:gap-12" data-id="element-2716">
                <motion.div {...fadeIn} transition={{
              delay: 0.1
            }} data-id="element-2717">
                  <Card className="h-full p-5 md:p-8 border-slate-200 bg-slate-50 hover:border-cyan-200 transition-colors" data-id="element-2718">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 md:mb-6" data-id="element-2719">
                      <Brain className="w-6 h-6 md:w-7 md:h-7 text-blue-600" data-id="element-2720" />
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-slate-900 mb-3 md:mb-4 uppercase tracking-wide" data-id="element-2721">
                      El Principiante Abrumado
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-lg" data-id="element-2722">
                      Quieres lanzar algo pero no sabes por dónde empezar. La
                      parálisis por análisis te consume. Ves a otros tener éxito
                      y te preguntas qué estás haciendo mal. El miedo a elegir
                      el nicho o producto equivocado te detiene antes de
                      empezar.
                    </p>
                  </Card>
                </motion.div>

                <motion.div {...fadeIn} transition={{
              delay: 0.2
            }} data-id="element-2723">
                  <Card className="h-full p-5 md:p-8 border-slate-200 bg-slate-50 hover:border-orange-200 transition-colors" data-id="element-2724">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-4 md:mb-6" data-id="element-2725">
                      <Flame className="w-6 h-6 md:w-7 md:h-7 text-orange-600" data-id="element-2726" />
                    </div>
                    <h3 className="text-lg md:text-xl font-black text-slate-900 mb-3 md:mb-4 uppercase tracking-wide" data-id="element-2727">
                      El Solopreneur Quemado
                    </h3>
                    <p className="text-slate-600 leading-relaxed text-lg" data-id="element-2728">
                      Ya has invertido en cursos, has probado modelos de negocio
                      y has gastado dinero en anuncios, pero nada es
                      consistente. Estás cansado de las promesas de "6 y 7
                      cifras" y solo quieres un sistema real que funcione sin
                      depender de la suerte o de un algoritmo.
                    </p>
                  </Card>
                </motion.div>
              </div>
            </div>
          </section>

          {/* 4.5 Video Carousel - Lo Que Aprenderás a Crear */}
          <section className="py-10 md:py-20 bg-slate-900 text-white overflow-hidden" data-id="element-2729">
            <div className="container mx-auto px-4 max-w-5xl" data-id="element-2730">
              <motion.div {...fadeIn} className="text-center mb-6 md:mb-12" data-id="element-2731">
                <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-4 inline-block" data-id="element-2732">
                  Resultados Reales
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-white mb-4" data-id="element-2733">
                  Esto Es Lo Que Aprenderás a Crear
                </h2>
                <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-2734">
                  Videos, contenido y productos digitales creados con el sistema
                  — desliza para ver más →
                </p>
              </motion.div>
            </div>

            <motion.div {...fadeIn} data-id="element-2735">
              <div className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 px-4 md:px-8 scrollbar-hide" style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }} data-id="element-2736">
                {/* Spacer for centering on large screens */}
                <div className="flex-shrink-0 w-0 md:w-[calc((100vw-1000px)/2)]" data-id="element-2737" />

                {/* Video 1 - UGC Vertical (9:16) */}
                <div className="flex-shrink-0 w-[65vw] md:w-[300px] snap-center" data-id="element-2738">
                  <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-xl" data-id="element-2739">
                    <div style={{
                  padding: '177.78% 0 0 0',
                  position: 'relative'
                }} data-id="element-2740">
                      <iframe src="https://player.vimeo.com/video/1168644203?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }} title="36 UGC" data-id="element-2741" />
                    </div>
                    <div className="p-4" data-id="element-2742">
                      <p className="text-sm font-semibold text-slate-300" data-id="element-2743">
                        UGC Creado con IA
                      </p>
                    </div>
                  </div>
                </div>

                {/* Video 2 - Vertical (9:16) */}
                <div className="flex-shrink-0 w-[65vw] md:w-[300px] snap-center" data-id="element-2744">
                  <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-xl" data-id="element-2745">
                    <div style={{
                  padding: '177.78% 0 0 0',
                  position: 'relative'
                }} data-id="element-2746">
                      <iframe src="https://player.vimeo.com/video/1164458576?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }} title="REEL 4 READY TO PLAY 4 SP" data-id="element-2747" />
                    </div>
                    <div className="p-4" data-id="element-2748">
                      <p className="text-sm font-semibold text-slate-300" data-id="element-2749">
                        Reel Creado con IA
                      </p>
                    </div>
                  </div>
                </div>

                {/* Video 3 - Vertical (9:16) */}
                <div className="flex-shrink-0 w-[65vw] md:w-[300px] snap-center" data-id="element-2750">
                  <div className="bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-xl" data-id="element-2751">
                    <div style={{
                  padding: '177.78% 0 0 0',
                  position: 'relative'
                }} data-id="element-2752">
                      <iframe src="https://player.vimeo.com/video/1168633813?badge=0&autopause=0&player_id=0&app_id=58479" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }} title="BACKYARD PIT MASTER" data-id="element-2753" />
                    </div>
                    <div className="p-4" data-id="element-2754">
                      <p className="text-sm font-semibold text-slate-300" data-id="element-2755">
                        Contenido Viral con IA
                      </p>
                    </div>
                  </div>
                </div>

                {/* Spacer for centering on large screens */}
                <div className="flex-shrink-0 w-4 md:w-[calc((100vw-1100px)/2)]" data-id="element-2756" />
              </div>
            </motion.div>
          </section>

          {/* 5. The Offer */}
          <section className="py-10 md:py-20 bg-slate-900 text-white relative" data-id="element-2757">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" data-id="element-2758"></div>
            <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-2759">
              <motion.div {...fadeIn} className="text-center mb-8 md:mb-12" data-id="element-2760">
                <h2 className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight" data-id="element-2761">
                  Activa el Sistema "48 Hrs AI Cash Machine" Hoy y Deja que la
                  IA Decida por Ti
                </h2>
                <p className="text-xl text-slate-300 leading-relaxed" data-id="element-2762">
                  La decisión real no es si esto funciona. Es si seguirás
                  decidiendo solo o dejarás que un sistema decida contigo. El
                  programa "48 Hrs AI Cash Machine" no es más información. Es un
                  sistema de ejecución que te guía paso a paso para que lances
                  tu primer (o próximo) producto digital en un fin de semana.
                </p>
              </motion.div>

              <motion.div {...fadeIn} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12 backdrop-blur-sm" data-id="element-2763">
                <h3 className="text-2xl font-bold text-cyan-400 mb-8 text-center uppercase tracking-wider" data-id="element-2764">
                  Qué Incluye El Sistema:
                </h3>

                <ul className="space-y-6 mb-10" data-id="element-2765">
                  {[{
                text: "Acceso al curso '48 Hrs AI Cash Machine' de por vida",
                value: '$2,997'
              }, {
                text: 'Las 8 Fases del Sistema de Lanzamiento con IA',
                value: '$1,997'
              }, {
                text: 'Comunidad Privada de Soporte 24/7',
                value: '$997'
              }, {
                text: '1 Llamada de Implementación en Vivo con Gabriel Beltrán al mes',
                value: '$1,500'
              }, {
                text: '1 Llamada de Coaching en Vivo con un Coach al mes',
                value: '$1,500'
              }].map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-2766">
                      <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-1" data-id="element-2767" />
                      <div data-id="element-2768">
                        <span className="text-lg md:text-xl font-medium text-white block" data-id="element-2769">
                          {item.text}
                        </span>
                        <span className="text-cyan-400/80 text-sm font-bold" data-id="element-2770">
                          Valor: {item.value}
                        </span>
                      </div>
                    </li>)}
                </ul>

                <div className="pt-8 border-t border-slate-700 text-center" data-id="element-2771">
                  <p className="text-slate-400 text-lg mb-2 uppercase tracking-widest font-bold" data-id="element-2772">
                    Valor Total del Sistema
                  </p>
                  <p className="text-4xl md:text-6xl font-black text-white line-through opacity-50 decoration-red-500" data-id="element-2773">
                    $11,476
                  </p>
                </div>
              </motion.div>
            </div>
          </section>

          {/* 6. Bonuses */}
          <section className="py-10 md:py-20 bg-slate-50" data-id="element-2774">
            <div className="container mx-auto px-4 max-w-5xl" data-id="element-2775">
              <motion.div {...fadeIn} className="text-center mb-8 md:mb-16" data-id="element-2776">
                <span className="bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-4 inline-block" data-id="element-2777">
                  Solo Disponibles Con La Grabación
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4" data-id="element-2778">
                  Bonos de Acción Rápida
                </h2>
                <p className="text-xl text-slate-600" data-id="element-2779">
                  Herramientas exclusivas de IA para acelerar tus resultados.
                </p>
              </motion.div>

              <div className="space-y-4 md:space-y-8" data-id="element-2780">
                <motion.div {...fadeIn} data-id="element-2781">
                  <BonusCard bonusNumber={1} title="AI Product Builder" description="Tu asistente completo para crear productos digitales desde cero. HeyLloyd te guía en cada paso: desde la idea inicial hasta el lanzamiento." features={['Valor: $497', 'Incluido GRATIS hoy']} imageSrc="https://cdn.magicpatterns.com/uploads/pXDmn28qLnCMrUYkdpGPpS/hf_20260116_225721_4dc7f3c1-48e8-4206-b7a5-3ddf70d36f02.png" data-id="element-2782" />
                </motion.div>

                <motion.div {...fadeIn} data-id="element-2783">
                  <BonusCard bonusNumber={2} title="Ebook Writer & Cover Designer" description="Perfecto para crear ebooks completos desde cero y diseñar portadas profesionales que atraen lectores." features={['Valor: $497', 'Incluido GRATIS hoy']} imageSrc="https://cdn.magicpatterns.com/uploads/mZHQRarDkUFPHSnnRgrYNx/heylloyd-image-1768596398723.png" data-id="element-2784" />
                </motion.div>

                <motion.div {...fadeIn} data-id="element-2785">
                  <BonusCard bonusNumber={3} title="Meta Ads Decision Engine" description="Motor de decisiones para tus campañas. Analiza, optimiza y toma decisiones estratégicas sobre tus anuncios en tiempo real." features={['Valor: $997', 'Incluido GRATIS hoy']} imageSrc="https://cdn.magicpatterns.com/uploads/uX7GKZjLzcZHYruWJanCH7/heylloyd-image-1768596648758.png" data-id="element-2786" />
                </motion.div>

                <motion.div {...fadeIn} data-id="element-2787">
                  <BonusCard bonusNumber={4} title="Sales Letter Weapon & Identity Persuasion Copywriter" description="Tu arma secreta para crear cartas de ventas que convierten y copy persuasivo que conecta con la identidad de tu audiencia." features={['Valor: $997', 'Incluido GRATIS hoy']} imageSrc="https://cdn.magicpatterns.com/uploads/5Vbgxv8pJwQBoVv72Jwat3/heylloyd-image-1768596561791.png" data-id="element-2788" />
                </motion.div>

                <motion.div {...fadeIn} data-id="element-2789">
                  <BonusCard bonusNumber={5} title="The Viral PDF Lab" description="Laboratorio especializado en crear PDFs que se comparten masivamente. Ideal para lead magnets y recursos descargables." features={['Valor: $497', 'Incluido GRATIS hoy']} imageSrc="https://cdn.magicpatterns.com/uploads/wRK7DAN3h4pP5mk1SskSzS/heylloyd-image-1768596692126.png" data-id="element-2790" />
                </motion.div>
              </div>
            </div>
          </section>

          {/* 8. Final CTA & Pricing */}
          <section id="pricing" className="py-12 md:py-24 bg-slate-900 text-white relative overflow-hidden scroll-mt-12" data-id="element-2791">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-cyan-900/20 via-slate-900 to-slate-900" data-id="element-2792"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" data-id="element-2793"></div>
            <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-2794">
              <motion.div {...fadeIn} className="text-center mb-6" data-id="element-2795">
                <span className="inline-block bg-red-500/10 text-red-400 border border-red-500/20 px-5 py-2 rounded-full text-sm font-bold tracking-widest uppercase mb-6" data-id="element-2796">
                  ⏰ Oferta por Tiempo Limitado
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-white leading-tight max-w-4xl mx-auto mb-4" data-id="element-2797">
                  Accede al Sistema Completo por Menos de lo que Cuesta un Curso
                  que No Vas a Terminar
                </h2>
                <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-2798">
                  Todo lo que necesitas para lanzar tu producto digital este fin
                  de semana — en un solo paquete.
                </p>
              </motion.div>

              {/* Product Image with glow */}
              <motion.div {...fadeIn} className="relative max-w-3xl mx-auto mb-8" data-id="element-2799">
                <div className="absolute inset-0 bg-cyan-500/8 blur-[80px] rounded-full scale-90" data-id="element-2800"></div>
                <div className="absolute -inset-4 bg-gradient-to-b from-cyan-500/5 to-transparent rounded-3xl" data-id="element-2801"></div>
              </motion.div>

              {/* Price Anchor */}
              <motion.div {...fadeIn} className="text-center mb-12" data-id="element-2802">
                <p className="text-slate-500 text-lg mb-1" data-id="element-2803">
                  Valor total del sistema + bonos:
                </p>
                <p className="text-3xl md:text-4xl font-black text-slate-500 line-through decoration-red-500/70 decoration-2" data-id="element-2804">
                  $11,476 USD
                </p>
                <p className="text-cyan-400 text-xl font-bold mt-3" data-id="element-2805">
                  Hoy accedes por una fracción:
                </p>
              </motion.div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-3xl mx-auto mb-12" data-id="element-2806">
                {/* Pricing Option 1 - Best Value */}
                <motion.div {...fadeIn} transition={{
              delay: 0.1
            }} data-id="element-2807">
                  <div className="relative h-full" data-id="element-2808">
                    {/* Glow border effect */}
                    <div className="absolute -inset-[1px] bg-gradient-to-b from-cyan-400 via-cyan-500/50 to-cyan-400/20 rounded-2xl" data-id="element-2809"></div>
                    <div className="relative h-full bg-slate-900 rounded-2xl p-8 flex flex-col" data-id="element-2810">
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-cyan-400 to-cyan-500 text-slate-900 text-xs font-black px-6 py-1.5 rounded-full uppercase tracking-widest shadow-lg shadow-cyan-500/30" data-id="element-2811">
                        ⭐ Mejor Valor
                      </div>
                      <h3 className="text-xl font-bold text-white mb-1 mt-2" data-id="element-2812">
                        PAGO ÚNICO
                      </h3>
                      <p className="text-slate-400 text-sm mb-6" data-id="element-2813">
                        Acceso inmediato y de por vida
                      </p>

                      <div className="mb-2" data-id="element-2814">
                        <span className="text-sm text-slate-500 line-through" data-id="element-2815">
                          $11,476
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 mb-2" data-id="element-2816">
                        <span className="text-6xl font-black text-cyan-400" data-id="element-2817">
                          $297
                        </span>
                        <span className="text-slate-500 text-lg" data-id="element-2818">USD</span>
                      </div>
                      <p className="text-emerald-400 text-sm font-semibold mb-8" data-id="element-2819">
                        Ahorras $11,179 — 97% de descuento
                      </p>

                      <div className="mt-auto" data-id="element-2820">
                        <a href="https://www.fanbasis.com/agency-checkout/true-marketer/wKOVJ" className="block w-full" data-id="element-2821">
                          <button className="w-full bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-900 font-black py-4 px-6 rounded-xl text-lg uppercase tracking-wide shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/40 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-2822">
                            COMPRAR AHORA
                            <ArrowRight className="w-5 h-5" data-id="element-2823" />
                          </button>
                        </a>
                        <p className="text-center text-slate-500 text-xs mt-3" data-id="element-2824">
                          🔒 Pago seguro • Acceso instantáneo
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Pricing Option 2 - Payment Plan */}
                <motion.div {...fadeIn} transition={{
              delay: 0.2
            }} data-id="element-2825">
                  <div className="relative h-full border border-slate-700 rounded-2xl bg-slate-800/40 backdrop-blur-sm p-8 flex flex-col hover:border-slate-600 transition-all duration-300" data-id="element-2826">
                    <h3 className="text-xl font-bold text-white mb-1" data-id="element-2827">
                      PLAN DE PAGOS
                    </h3>
                    <p className="text-slate-400 text-sm mb-6" data-id="element-2828">
                      Con la opción ZIP al momento del checkout (Solo US)
                    </p>

                    <div className="flex items-baseline gap-1 mb-1" data-id="element-2829">
                      <span className="text-lg text-slate-400" data-id="element-2830">4 pagos de</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-2831">
                      <span className="text-6xl font-black text-white" data-id="element-2832">
                        $75<span className="text-4xl" data-id="element-2833">.75</span>
                      </span>
                      <span className="text-slate-500 text-lg" data-id="element-2834">USD</span>
                    </div>
                    <p className="text-slate-400 text-sm mb-8" data-id="element-2835">
                      Cobrados cada 15 días (Total: $303)
                    </p>

                    <div className="mt-auto" data-id="element-2836">
                      <a href="https://www.fanbasis.com/agency-checkout/true-marketer/wKOVJ" className="block w-full" data-id="element-2837">
                        <button className="w-full border-2 border-cyan-500/40 hover:border-cyan-400 text-cyan-400 hover:text-cyan-300 font-black py-4 px-6 rounded-xl text-lg uppercase tracking-wide transition-all duration-300 flex items-center justify-center gap-2 hover:bg-cyan-500/5" data-id="element-2838">
                          INICIAR PLAN DE PAGOS
                          <ArrowRight className="w-5 h-5" data-id="element-2839" />
                        </button>
                      </a>
                      <p className="text-center text-slate-500 text-xs mt-3" data-id="element-2840">
                        🔒 Pago seguro • Sin compromiso
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* What you get summary */}
              <motion.div {...fadeIn} className="max-w-3xl mx-auto mb-12" data-id="element-2841">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center" data-id="element-2842">
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5" data-id="element-2843">
                    <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-2844">
                      <GraduationCap className="w-5 h-5 text-cyan-400" data-id="element-2845" />
                    </div>
                    <p className="text-white font-bold text-sm" data-id="element-2846">
                      Curso Completo
                    </p>
                    <p className="text-slate-500 text-xs" data-id="element-2847">De por vida</p>
                  </div>
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5" data-id="element-2848">
                    <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-2849">
                      <Bot className="w-5 h-5 text-cyan-400" data-id="element-2850" />
                    </div>
                    <p className="text-white font-bold text-sm" data-id="element-2851">
                      8 Fases con IA
                    </p>
                    <p className="text-slate-500 text-xs" data-id="element-2852">Paso a paso</p>
                  </div>
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5" data-id="element-2853">
                    <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-2854">
                      <Users className="w-5 h-5 text-cyan-400" data-id="element-2855" />
                    </div>
                    <p className="text-white font-bold text-sm" data-id="element-2856">
                      Comunidad 24/7
                    </p>
                    <p className="text-slate-500 text-xs" data-id="element-2857">Soporte real</p>
                  </div>
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-5" data-id="element-2858">
                    <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3" data-id="element-2859">
                      <PhoneCall className="w-5 h-5 text-cyan-400" data-id="element-2860" />
                    </div>
                    <p className="text-white font-bold text-sm" data-id="element-2861">
                      2 Llamadas/Mes
                    </p>
                    <p className="text-slate-500 text-xs" data-id="element-2862">Con Gabriel</p>
                  </div>
                </div>
              </motion.div>

              {/* Guarantee */}
              <motion.div {...fadeIn} className="max-w-3xl mx-auto mb-16" data-id="element-2863">
                <div className="bg-gradient-to-r from-emerald-500/5 via-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6" data-id="element-2864">
                  <div className="w-24 h-24 bg-emerald-500/10 rounded-full flex items-center justify-center flex-shrink-0 border border-emerald-500/20" data-id="element-2865">
                    <ShieldCheck className="w-12 h-12 text-emerald-400" data-id="element-2866" />
                  </div>
                  <div className="text-center md:text-left" data-id="element-2867">
                    <h4 className="text-2xl font-black text-white mb-2" data-id="element-2868">
                      Garantía Incondicional de 30 Días
                    </h4>
                    <p className="text-slate-300 leading-relaxed text-lg" data-id="element-2869">
                      Implementa el sistema. Si no es lo que prometí o no te
                      gusta, te devuelvo tu dinero.{' '}
                      <span className="text-emerald-400 font-bold" data-id="element-2870">
                        Sin preguntas. Sin letra pequeña.
                      </span>{' '}
                      Tienes un mes entero para probarlo sin riesgo.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Final Urgency & Timer */}
              <motion.div {...fadeIn} className="text-center max-w-2xl mx-auto" data-id="element-2871">
                <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8" data-id="element-2872">
                  <p className="text-red-400 font-bold text-xl mb-6" data-id="element-2873">
                    ⚠️ Esta página y la oferta desaparecerán cuando el contador
                    llegue a cero.
                  </p>
                  <p className="text-slate-400 mb-6" data-id="element-2874">
                    Esta es tu última oportunidad de acceder a este precio.
                  </p>
                  <div className="bg-slate-950/50 p-6 rounded-2xl border border-slate-800 inline-block mb-6" data-id="element-2875">
                    <CountdownTimer data-id="element-2876" />
                  </div>
                  <div className="pt-4" data-id="element-2877">
                    <a href="https://www.fanbasis.com/agency-checkout/true-marketer/wKOVJ" className="inline-block" data-id="element-2878">
                      <button className="bg-gradient-to-r from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-900 font-black py-4 px-12 rounded-xl text-lg uppercase tracking-wide shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/40 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-2879">
                        QUIERO ACCEDER AHORA
                        <ArrowRight className="w-5 h-5" data-id="element-2880" />
                      </button>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* 8.5 FAQ */}
          <section className="py-10 md:py-20 bg-white" data-id="element-2881">
            <div className="container mx-auto px-4 max-w-5xl" data-id="element-2882">
              <FAQ data-id="element-2883" />
            </div>
          </section>

          {/* 9. Footer */}
          <Footer data-id="element-2884" />
        </>}

      {/* Always-visible Footer */}
      {!showContent && <Footer data-id="element-2885" />}

      {/* Floating WhatsApp Button */}
      <a href="https://wa.link/infocommerce" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BD5A] w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-lg shadow-[#25D366]/30 hover:scale-110 transition-all duration-300" aria-label="Chat on WhatsApp" data-id="element-2886">
        <svg className="w-8 h-8 md:w-9 md:h-9 text-white" viewBox="0 0 24 24" fill="currentColor" data-id="element-2887">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-2888" />
        </svg>
      </a>
    </div>;
}